// Baselayer Background Checks — "Run check" modal. A type picker drives a
// subject form (auto-filled from the linked deal when a dealId is supplied),
// plus the KYB add-on toggles. Submitting fires the run mutation and navigates
// to the new check's detail page.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Label } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useDeal } from "@/hooks/deals/useDeals";
import { useRunBackgroundCheck } from "@/hooks/backgroundChecks/useBackgroundChecks";
import type {
  BackgroundCheckType,
  RunBackgroundCheckRequest,
} from "@/api/backgroundChecks/backgroundChecksApi";
import { CHECK_TYPE_LABELS } from "./checkFormat";

export interface RunCheckPrefillAddress {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

interface RunCheckModalProps {
  open: boolean;
  onClose: () => void;
  /** When present, the run is linked to this deal and the subject auto-fills. */
  dealId?: string | null;
  /** When present, the run is linked to this loan (loan-tab origination
   *  context — lets the lien-search mirror resolve the exact loan instead
   *  of falling back to Deal→first-loan). */
  loanId?: string | null;
  /** Check type pre-selected when the modal opens (defaults to KYB). */
  initialType?: BackgroundCheckType;
  /** Seeds the address field while it is still empty (never clobbers edits). */
  prefillAddress?: RunCheckPrefillAddress | null;
  /** Verified-business offer. Pass the newest completed KYB's Baselayer
   *  business id (when known) and subject name to render a one-click
   *  "Use verified business: {name}" shortcut. `knownBusinessName === null`
   *  means the caller looked and found NO completed KYB for this deal —
   *  the lien-search form then surfaces the KYB-prerequisite notice.
   *  Leaving both props undefined disables the feature (existing surfaces). */
  knownBusinessId?: string | null;
  knownBusinessName?: string | null;
}

const TYPE_ORDER: BackgroundCheckType[] = [
  "Kyb",
  "Ofac",
  "Pep",
  "Dmf",
  "Online",
  "LienSearch",
];

const selectClass =
  "px-space-3 py-[10px] border border-border-light rounded-md bg-white-pure text-small text-onyx " +
  "focus:outline-none focus:border-gold focus:shadow-goldRing";

function formatPrefillAddress(
  prefill: RunCheckPrefillAddress | null | undefined,
): string {
  if (!prefill) return "";
  return [prefill.street, prefill.city, prefill.state, prefill.zip]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(", ");
}

export function RunCheckModal({
  open,
  onClose,
  dealId,
  loanId,
  initialType = "Kyb",
  prefillAddress,
  knownBusinessId,
  knownBusinessName,
}: RunCheckModalProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const run = useRunBackgroundCheck();
  const dealQuery = useDeal(dealId ?? undefined);

  const [type, setType] = useState<BackgroundCheckType>("Kyb");
  const [subjectKind, setSubjectKind] = useState("Business");
  const [subjectName, setSubjectName] = useState("");
  const [address, setAddress] = useState("");
  const [tin, setTin] = useState("");
  const [ssn, setSsn] = useState("");
  const [baselayerBusinessId, setBaselayerBusinessId] = useState("");
  const [officerNames, setOfficerNames] = useState("");
  const [lienSearchStates, setLienSearchStates] = useState("");
  const [websiteAnalysis, setWebsiteAnalysis] = useState(false);
  const [naicsPrediction, setNaicsPrediction] = useState(false);
  const [enhanced, setEnhanced] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-fill the subject from the linked deal's business once it loads, but
  // only while the field is still empty so we never clobber staff edits.
  const dealBusinessName = dealQuery.data?.businessNameInitial ?? null;
  useEffect(() => {
    if (open && dealBusinessName && !subjectName) {
      setSubjectName(dealBusinessName);
    }
  }, [open, dealBusinessName, subjectName]);

  // Seed the address from the caller's prefill once available, but only
  // while the field is still empty (same discipline as the subject name).
  const formattedPrefillAddress = formatPrefillAddress(prefillAddress);
  useEffect(() => {
    if (open && formattedPrefillAddress && !address) {
      setAddress(formattedPrefillAddress);
    }
  }, [open, formattedPrefillAddress, address]);

  // Reset all fields each time the modal opens so a prior run doesn't bleed in.
  useEffect(() => {
    if (!open) return;
    setType(initialType);
    setSubjectKind("Business");
    setSubjectName(dealBusinessName ?? "");
    setAddress(formattedPrefillAddress);
    setTin("");
    setSsn("");
    setBaselayerBusinessId("");
    setOfficerNames("");
    setLienSearchStates("");
    setWebsiteAnalysis(false);
    setNaicsPrediction(false);
    setEnhanced(false);
    setError(null);
    // dealBusinessName / formattedPrefillAddress / initialType intentionally
    // excluded — late-arriving prefill re-seeds via the effects above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const useVerifiedBusiness = () => {
    if (!knownBusinessName) return;
    setSubjectKind("Business");
    setSubjectName(knownBusinessName);
    if (knownBusinessId) setBaselayerBusinessId(knownBusinessId);
  };

  const isKyb = type === "Kyb";
  const isLienSearch = type === "LienSearch";

  const splitList = (value: string): string[] =>
    value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);

  const body = useMemo<RunBackgroundCheckRequest>(
    () => ({
      type,
      subjectKind,
      subjectName: subjectName.trim(),
      dealId: dealId ?? undefined,
      loanId: loanId ?? undefined,
      address: address.trim() || undefined,
      tin: tin.trim() || undefined,
      ssn: ssn.trim() || undefined,
      officerNames: isKyb ? splitList(officerNames) : undefined,
      baselayerBusinessId: baselayerBusinessId.trim() || undefined,
      lienSearchStates: isLienSearch ? splitList(lienSearchStates) : undefined,
      websiteAnalysis: isKyb && websiteAnalysis,
      naicsPrediction: isKyb && naicsPrediction,
      enhanced: isKyb && enhanced,
    }),
    [
      type,
      subjectKind,
      subjectName,
      dealId,
      loanId,
      address,
      tin,
      ssn,
      isKyb,
      officerNames,
      baselayerBusinessId,
      isLienSearch,
      lienSearchStates,
      websiteAnalysis,
      naicsPrediction,
      enhanced,
    ],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!body.subjectName) {
      setError("Subject name is required.");
      return;
    }
    try {
      const result = await run.mutateAsync(body);
      toast({ message: "Background check submitted.", tone: "success" });
      onClose();
      navigate(`/background-checks/${result.id}`);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.errors[0] ?? err.message
          : err instanceof Error
            ? err.message
            : "Could not run the background check.";
      setError(message);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Run background check"
      description={
        dealId
          ? "This check is linked to the current deal."
          : "Submit a new check to Baselayer."
      }
      size="md"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="run-check-form"
            loading={run.isPending}
            disabled={run.isPending}
            data-testid="run-check-submit"
          >
            Run check
          </Button>
        </>
      }
    >
      <form
        id="run-check-form"
        onSubmit={handleSubmit}
        className="flex flex-col gap-space-4"
      >
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="bgc-type">Check type</Label>
          <select
            id="bgc-type"
            className={selectClass}
            value={type}
            onChange={(e) => setType(e.target.value as BackgroundCheckType)}
            data-testid="run-check-type"
          >
            {TYPE_ORDER.map((t) => (
              <option key={t} value={t}>
                {CHECK_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
          <div className="flex flex-col gap-space-1">
            <Label htmlFor="bgc-subject-kind">Subject kind</Label>
            <select
              id="bgc-subject-kind"
              className={selectClass}
              value={subjectKind}
              onChange={(e) => setSubjectKind(e.target.value)}
            >
              <option value="Business">Business</option>
              <option value="Person">Person</option>
              <option value="Entity">Entity</option>
            </select>
          </div>
          <div className="flex flex-col gap-space-1">
            <Label htmlFor="bgc-subject-name" required>
              Subject name
            </Label>
            <Input
              id="bgc-subject-name"
              value={subjectName}
              onChange={(e) => setSubjectName(e.target.value)}
              required
              data-testid="run-check-subject-name"
            />
          </div>
        </div>

        {knownBusinessName && (
          <button
            type="button"
            className="self-start text-small text-gold-shadow hover:underline"
            onClick={useVerifiedBusiness}
            data-testid="run-check-use-verified-business"
          >
            Use verified business: {knownBusinessName}
          </button>
        )}

        {knownBusinessName === null && isLienSearch && (
          <p
            className="text-small text-gray-mid m-0"
            data-testid="run-check-kyb-prerequisite"
          >
            No verified business yet for this deal — run a KYB check first.
            Switch the check type above to KYB (Business) to start one.
          </p>
        )}

        <div className="flex flex-col gap-space-1">
          <Label htmlFor="bgc-address">Address</Label>
          <Input
            id="bgc-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
          <div className="flex flex-col gap-space-1">
            <Label htmlFor="bgc-tin">TIN</Label>
            <Input
              id="bgc-tin"
              value={tin}
              onChange={(e) => setTin(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-space-1">
            <Label htmlFor="bgc-ssn">SSN</Label>
            <Input
              id="bgc-ssn"
              value={ssn}
              onChange={(e) => setSsn(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-space-1">
          <Label htmlFor="bgc-baselayer-business-id">
            Baselayer business ID
          </Label>
          <Input
            id="bgc-baselayer-business-id"
            value={baselayerBusinessId}
            onChange={(e) => setBaselayerBusinessId(e.target.value)}
            placeholder="Optional — re-use an existing Baselayer business"
          />
        </div>

        {isKyb && (
          <div className="flex flex-col gap-space-1">
            <Label htmlFor="bgc-officer-names">Officer names</Label>
            <Input
              id="bgc-officer-names"
              value={officerNames}
              onChange={(e) => setOfficerNames(e.target.value)}
              placeholder="Comma-separated"
            />
          </div>
        )}

        {isLienSearch && (
          <div className="flex flex-col gap-space-1">
            <Label htmlFor="bgc-lien-states">Lien search states</Label>
            <Input
              id="bgc-lien-states"
              value={lienSearchStates}
              onChange={(e) => setLienSearchStates(e.target.value)}
              placeholder="Comma-separated, e.g. AZ, CA"
            />
          </div>
        )}

        {isKyb && (
          <fieldset className="flex flex-col gap-space-2 border border-border-light rounded-md p-space-3">
            <legend className="font-display text-label font-semibold uppercase tracking-label text-gray-mid px-space-1">
              KYB add-ons
            </legend>
            <AddOnCheckbox
              id="bgc-website"
              label="Website analysis"
              checked={websiteAnalysis}
              onChange={setWebsiteAnalysis}
            />
            <AddOnCheckbox
              id="bgc-naics"
              label="NAICS prediction"
              checked={naicsPrediction}
              onChange={setNaicsPrediction}
            />
            <AddOnCheckbox
              id="bgc-enhanced"
              label="Enhanced verification"
              checked={enhanced}
              onChange={setEnhanced}
            />
          </fieldset>
        )}

        {error && (
          <p
            className="text-small text-status-error m-0"
            role="alert"
            data-testid="run-check-error"
          >
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}

interface AddOnCheckboxProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}

function AddOnCheckbox({ id, label, checked, onChange }: AddOnCheckboxProps) {
  return (
    <label htmlFor={id} className="flex items-center gap-space-2 text-small text-onyx">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-gold"
      />
      {label}
    </label>
  );
}
